import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MapPin, Calendar, Phone, Mail, ChevronLeft,
  Dog, Filter, Search, X, Check, Home, Users, PawPrint,
  Shield, Syringe, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sample adoption dogs for demo
const DEMO_ADOPTIONS = [
  {
    id: 'adopt_demo1',
    name: 'Buddy',
    breed: 'Labrador Mix',
    age: 3,
    gender: 'male',
    size: 'large',
    description: 'Buddy is a friendly, energetic boy who loves playing fetch and going for long walks. He\'s great with kids and other dogs!',
    personality: ['Friendly', 'Playful', 'Loyal'],
    good_with_kids: true,
    good_with_dogs: true,
    good_with_cats: false,
    photos: ['https://images.pexels.com/photos/1490908/pexels-photo-1490908.jpeg?auto=compress&cs=tinysrgb&w=800'],
    shelter_name: 'RSPCA Queensland',
    location: { suburb: 'Brisbane', state: 'QLD' },
    adoption_fee: 350,
    vaccinated: true,
    desexed: true,
    microchipped: true
  },
  {
    id: 'adopt_demo2',
    name: 'Luna',
    breed: 'Border Collie',
    age: 2,
    gender: 'female',
    size: 'medium',
    description: 'Luna is an intelligent and active girl looking for a home with a yard. She knows basic commands and loves to learn new tricks.',
    personality: ['Smart', 'Active', 'Eager to please'],
    good_with_kids: true,
    good_with_dogs: true,
    good_with_cats: true,
    photos: ['https://images.pexels.com/photos/551628/pexels-photo-551628.jpeg?auto=compress&cs=tinysrgb&w=800'],
    shelter_name: 'Sydney Dogs & Cats Home',
    location: { suburb: 'Sydney', state: 'NSW' },
    adoption_fee: 400,
    vaccinated: true,
    desexed: true,
    microchipped: true
  },
  {
    id: 'adopt_demo3',
    name: 'Max',
    breed: 'Staffy Cross',
    age: 5,
    gender: 'male',
    size: 'medium',
    description: 'Max is a gentle soul who loves cuddles on the couch. He\'s calm, house-trained, and perfect for a quieter household.',
    personality: ['Calm', 'Affectionate', 'Gentle'],
    good_with_kids: true,
    good_with_dogs: false,
    good_with_cats: false,
    photos: ['https://images.pexels.com/photos/2023384/pexels-photo-2023384.jpeg?auto=compress&cs=tinysrgb&w=800'],
    shelter_name: 'Lost Dogs Home',
    location: { suburb: 'Melbourne', state: 'VIC' },
    adoption_fee: 300,
    vaccinated: true,
    desexed: true,
    microchipped: true
  }
];

// Enquiry Modal
const EnquiryModal = ({ dog, isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', message: '',
    living_situation: '', has_yard: '', other_pets: '', experience: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/adoptions/${dog.id}/enquire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('Enquiry submitted!');
      } else {
        // Demo mode
        setSubmitted(true);
        toast.success('Enquiry submitted! (Demo)');
      }
    } catch (error) {
      setSubmitted(true);
      toast.success('Enquiry submitted! (Demo)');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#4E876C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#4E876C]" />
            </div>
            <h3 className="text-xl font-bold text-[#1A1817] mb-2">Enquiry Sent!</h3>
            <p className="text-[#7A7571] mb-4">
              {dog.shelter_name} will contact you soon about {dog.name}.
            </p>
            <Button onClick={onClose} className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl">
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#1A1817]">
                Enquire about {dog.name}
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#7A7571] mb-1">Name *</label>
                  <Input
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#7A7571] mb-1">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#7A7571] mb-1">Email *</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="rounded-xl"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#7A7571] mb-1">Living Situation</label>
                <select
                  value={form.living_situation}
                  onChange={e => setForm(p => ({ ...p, living_situation: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0]"
                >
                  <option value="">Select...</option>
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="rural">Rural Property</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#7A7571] mb-1">Do you have a yard?</label>
                <div className="flex gap-4">
                  {['Yes', 'No'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, has_yard: opt.toLowerCase() }))}
                      className={`flex-1 py-2 px-4 rounded-xl font-medium transition ${
                        form.has_yard === opt.toLowerCase() ? 'bg-[#4E876C] text-white' : 'bg-[#F5F3EE] text-[#7A7571]'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#7A7571] mb-1">Message</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Tell us why you'd be a great match..."
                  className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
              >
                {submitting ? 'Submitting...' : 'Submit Enquiry'}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// Dog Card Component
const AdoptionCard = ({ dog, onEnquire }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]"
    >
      <div className="relative h-56">
        <img
          src={dog.photos?.[0] || 'https://images.pexels.com/photos/1805164/pexels-photo-1805164.jpeg'}
          alt={dog.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3 bg-[#FF7555] text-white text-sm px-3 py-1 rounded-full font-medium">
          ${dog.adoption_fee}
        </div>
        {dog.vaccinated && dog.desexed && (
          <div className="absolute top-3 left-3 bg-[#4E876C] text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Shield className="w-3 h-3" /> Vet Checked
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {dog.name}
            </h3>
            <p className="text-sm text-[#7A7571]">{dog.breed} • {dog.age} yrs • {dog.gender}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            dog.size === 'small' ? 'bg-[#F4D35E]/20 text-[#B8960A]' :
            dog.size === 'medium' ? 'bg-[#6B8EAE]/20 text-[#5A7A96]' :
            'bg-[#9B6B9E]/20 text-[#7D5680]'
          }`}>
            {dog.size}
          </span>
        </div>

        <p className="text-sm text-[#7A7571] mb-3 line-clamp-2">{dog.description}</p>

        <div className="flex items-center gap-2 text-xs text-[#7A7571] mb-3">
          <MapPin className="w-3 h-3" />
          {dog.location?.suburb}, {dog.location?.state}
          <span className="mx-1">•</span>
          {dog.shelter_name}
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {dog.good_with_kids && (
            <span className="text-xs bg-[#4E876C]/10 text-[#4E876C] px-2 py-1 rounded-full flex items-center gap-1">
              <Users className="w-3 h-3" /> Kids OK
            </span>
          )}
          {dog.good_with_dogs && (
            <span className="text-xs bg-[#FF7555]/10 text-[#FF7555] px-2 py-1 rounded-full flex items-center gap-1">
              <Dog className="w-3 h-3" /> Dogs OK
            </span>
          )}
          {dog.good_with_cats && (
            <span className="text-xs bg-[#6B8EAE]/10 text-[#6B8EAE] px-2 py-1 rounded-full">
              Cats OK
            </span>
          )}
        </div>

        <Button
          onClick={() => onEnquire(dog)}
          className="w-full h-10 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-medium"
        >
          <Heart className="w-4 h-4 mr-2" /> Enquire to Adopt
        </Button>
      </div>
    </motion.div>
  );
};

// Main Adoptions Page
export default function PupAdoptions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [enquiryDog, setEnquiryDog] = useState(null);
  const [filters, setFilters] = useState({
    breed: '', state: '', size: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Check auth
    fetch(`${API_URL}/api/puppoto/auth/me`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => setUser(data))
      .catch(() => {});

    // Fetch adoptions
    fetchDogs();
  }, []);

  const fetchDogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.breed) params.append('breed', filters.breed);
      if (filters.state) params.append('location', filters.state);
      
      const res = await fetch(`${API_URL}/api/puppoto/adoptions?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDogs(data.dogs?.length > 0 ? data.dogs : DEMO_ADOPTIONS);
      } else {
        setDogs(DEMO_ADOPTIONS);
      }
    } catch (error) {
      setDogs(DEMO_ADOPTIONS);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/puppoto/adopt';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#EBE8E0]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Adopt a Dog
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            {user ? (
              <button 
                onClick={() => navigate('/puppoto/profile')}
                className="w-10 h-10 rounded-full bg-[#F5F3EE] flex items-center justify-center overflow-hidden"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#7A7571]" />
                )}
              </button>
            ) : (
              <button 
                onClick={handleGoogleLogin}
                className="w-10 h-10 rounded-full bg-[#FF7555] flex items-center justify-center"
              >
                <LogIn className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#FF7555]/10 to-[#F4D35E]/10 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Give a Dog a <span className="text-[#FF7555]">Forever Home</span>
          </h1>
          <p className="text-lg text-[#7A7571] max-w-2xl mx-auto">
            Thousands of loving dogs are waiting in shelters across Australia. 
            Find your perfect companion and change a life today.
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7571]" />
            <Input
              placeholder="Search by breed..."
              value={filters.breed}
              onChange={e => setFilters(p => ({ ...p, breed: e.target.value }))}
              className="pl-10 rounded-xl border-[#EBE8E0]"
            />
          </div>
          
          <select
            value={filters.state}
            onChange={e => setFilters(p => ({ ...p, state: e.target.value }))}
            className="h-10 px-4 rounded-xl border border-[#EBE8E0] bg-white"
          >
            <option value="">All States</option>
            <option value="NSW">NSW</option>
            <option value="VIC">VIC</option>
            <option value="QLD">QLD</option>
            <option value="WA">WA</option>
            <option value="SA">SA</option>
            <option value="TAS">TAS</option>
            <option value="NT">NT</option>
            <option value="ACT">ACT</option>
          </select>

          <Button
            onClick={fetchDogs}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            Search
          </Button>
        </div>
      </div>

      {/* Dogs Grid */}
      <main className="max-w-6xl mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#FF7555] border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dogs.map(dog => (
              <AdoptionCard
                key={dog.id}
                dog={dog}
                onEnquire={setEnquiryDog}
              />
            ))}
          </div>
        )}
      </main>

      {/* Enquiry Modal */}
      <AnimatePresence>
        {enquiryDog && (
          <EnquiryModal
            dog={enquiryDog}
            isOpen={!!enquiryDog}
            onClose={() => setEnquiryDog(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
